import { bomClient } from '../../../client/bomClient';
import { RunMarker } from '../../../types/bom.types';

/**
 * The validation check against its persisted run marker. A check runs once:
 * when the server says one is already going (202, or a RUNNING marker found
 * on arrival) the page waits on the marker instead of starting another —
 * a refresh never pays for a second model call.
 */
const POLL_MS = 3000;
/** A marker still RUNNING after this is treated as a check that did not finish. */
const GIVE_UP_MS = 5 * 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export class ValidationRunFailed extends Error {}

type Analysis = Awaited<ReturnType<typeof bomClient.getAnalysisStatus>>;

/** Polls GET /analysis until the validation marker settles; resolves with the analysis. */
export const awaitValidationRun = async (bomId: string, pollMs = POLL_MS): Promise<Analysis> => {
  const deadline = Date.now() + GIVE_UP_MS;
  for (;;) {
    const analysis = await bomClient.getAnalysisStatus(bomId);
    const marker: RunMarker | null | undefined = analysis?.validationRun;
    if (marker?.status === 'FAILED') throw new ValidationRunFailed(marker.error?.message || 'The check failed');
    if (marker?.status !== 'RUNNING') return analysis;
    if (Date.now() > deadline) throw new ValidationRunFailed('The check did not finish');
    await sleep(pollMs);
  }
};

/** Starts a check (or joins the one running) and resolves with the fresh analysis. */
export const runValidationOnce = async (bomId: string, pollMs = POLL_MS): Promise<Analysis> => {
  const result = await bomClient.runValidationAndRecommendation(bomId);
  if (result?.validationRun?.status === 'RUNNING') return awaitValidationRun(bomId, pollMs);
  return bomClient.getAnalysisStatus(bomId);
};
